import React from 'react'
import { Modal } from 'antd'
import './denominationModal.scss'

// Utility function to format denomination display properly
const formatDenominationDisplay = (value) => {
  const denomValue = parseFloat(value)
  if (denomValue >= 1) {
    return denomValue.toFixed(0)
  } else {
    return denomValue.toFixed(2)
  }
}

// Utility function to format currency with decimals
const formatCurrencyWithDecimals = (value) => {
  if (value === null || value === undefined) return ''
  return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const DenominationModal = ({
  visible,
  onClose,
  title = 'Cash by Denomination',
  denominationData = {},
  systemCashOnHand = 0,
  actualCashOnHand = 0,
  showComparison = true,
  className = 'denomination-modal'
}) => {
  // Calculate discrepancy
  const discrepancyValue = actualCashOnHand - systemCashOnHand

  // Get denomination data in the correct format
  const getDenominationData = () => {
    if (!denominationData || denominationData.length === 0) {
      return []
    }

    // Handle different possible data structures
    if (Array.isArray(denominationData)) {
      // If it's an array of denomination objects (already processed)
      return denominationData
        .filter(item => (item.value || item.denomination) > 0) // Remove invalid denominations
        .map(item => ({
          value: parseFloat(item.value || item.denomination || 0),
          count: item.count || item.quantity || 0,
          total: item.total || ((parseFloat(item.value || item.denomination || 0)) * (item.count || item.quantity || 0))
        }))
        .sort((a, b) => b.value - a.value) // Sort by denomination value (descending)
    } else if (typeof denominationData === 'object') {
      // If it's an object with denomination keys
      return Object.entries(denominationData)
        .map(([key, count]) => {
          const value = parseFloat(key)
          return {
            value: value,
            count: count || 0,
            total: value * (count || 0)
          }
        })
        .filter(item => item.value > 0) // Remove invalid denominations
        .sort((a, b) => b.value - a.value) // Sort by denomination value (descending)
    }

    return []
  }

  const processedDenominationData = getDenominationData()

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      footer={[]}
      width={500}
      className={className}
    >
      <div className="denomination-modal-content">
        <table className="denomination-table">
          <thead>
            <tr>
              <th>Denomination</th>
              <th style={{ textAlign: 'center' }}>Count</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {processedDenominationData.map((item, index) => (
              <tr key={index} className={item.count > 0 ? 'has-count' : ''}>
                <td className="denom-value">₱{formatDenominationDisplay(item.value)}</td>
                <td className="denom-count" style={{ textAlign: 'center' }}>{item.count}</td>
                <td className="denom-total">₱{formatCurrencyWithDecimals(item.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {showComparison && (
              <>
                <tr className="table-footer">
                  <td colSpan="2" className="total-label">
                    <strong>System Cash on Hand</strong>
                  </td>
                  <td className="total-value">
                    ₱{formatCurrencyWithDecimals(systemCashOnHand)}
                  </td>
                </tr>
                {actualCashOnHand > 0 && (
                  <>
                    <tr className="table-footer">
                      <td colSpan="2" className="total-label">
                        <strong>Actual Cash on Hand</strong>
                      </td>
                      <td className="total-value">
                        ₱{formatCurrencyWithDecimals(actualCashOnHand)}
                      </td>
                    </tr>
                    <tr className="table-footer">
                      <td colSpan="2" className="total-label">
                        <strong>
                          {discrepancyValue > 0 ? 'Surplus' : discrepancyValue < 0 ? 'Discrepancy' : 'Balanced'}
                        </strong>
                      </td>
                      <td className={`total-value ${discrepancyValue > 0 ? 'surplus' :
                        discrepancyValue < 0 ? 'discrepancy' : ''
                        }`}>
                        ₱{formatCurrencyWithDecimals(Math.abs(discrepancyValue))}
                      </td>
                    </tr>
                  </>
                )}
              </>
            )}
            {!showComparison && (
              <tr className="table-footer">
                <td colSpan="2" className="total-label">
                  <strong>Total Amount</strong>
                </td>
                <td className="total-value">
                  ₱{formatCurrencyWithDecimals(actualCashOnHand || processedDenominationData.reduce((total, item) => total + item.total, 0))}
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    </Modal>
  )
}

export default DenominationModal